import {
  EventCreatePayload,
  IEventListItem,
  IEventTicketListItem,
  IPaginatedResponse,
  IQrCodeStats,
  IQrCodeUploadResult,
  IResponse,
  IUserEvenTicketListItem,
} from '@types';
import { httpClient } from '../http-client';

export const getEventList = async ([page, search, time]): Promise<IPaginatedResponse<IEventListItem>> => {
  const response = await httpClient.get<IPaginatedResponse<IEventListItem>>('event/list', {
    params: {
      time: time,
      search,
      page: page,
      limit: 10,
    },
  });
  return response.data;
};

export const getEvent = async (id: string): Promise<IResponse<IEventListItem>> => {
  const response = await httpClient.get<IResponse<IEventListItem>>('event', {
    params: {
      id,
    },
  });
  return response.data;
};

export const createEvent = async (payload: EventCreatePayload): Promise<IEventListItem> => {
  const response = await httpClient.post<IEventListItem>('event/create', payload, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  return response.data;
};

export const updateEvent = async (id: string, payload: EventCreatePayload): Promise<IEventListItem> => {
  const response = await httpClient.put<IEventListItem>(`event/${id}`, payload, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  return response.data;
};

export const deleteEvent = async (id: string): Promise<IResponse<{ id: string; success: boolean }>> => {
  const response = await httpClient.delete<IResponse<{ id: string; success: boolean }>>('event/delete', {
    data: { id },
  });

  return response.data;
};

export const deliverTicket = async (id: string): Promise<IResponse<boolean>> => {
  const response = await httpClient.put<IResponse<boolean>>(`tickets/${id}/deliver`);

  return response.data;
};

export const bulkDeliverTickets = async (eventId: string, ticketIds: string[]): Promise<IResponse<number>> => {
  const response = await httpClient.post<IResponse<number>>(`tickets/${eventId}/bulk_deliver`, { ticketIds });

  return response.data;
};

export const getQrCodeStats = async (eventId: string): Promise<IResponse<IQrCodeStats>> => {
  const response = await httpClient.get<IResponse<IQrCodeStats>>(`tickets/${eventId}/qr_codes/stats`);

  return response.data;
};

export const uploadQrCodes = async (eventId: string, file: File): Promise<IResponse<IQrCodeUploadResult>> => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await httpClient.post<IResponse<IQrCodeUploadResult>>(`tickets/${eventId}/qr_codes`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  return response.data;
};

// Throws on failure; the error body is a Blob because of responseType.
export const exportUnusedQrCodes = async (eventId: string): Promise<void> => {
  const response = await httpClient.get<Blob>(`tickets/${eventId}/qr_codes/export_unused`, {
    responseType: 'blob',
  });

  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `qr-codes-unused-${new Date().toISOString().slice(0, 10)}.xlsx`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export const toggleTicketPaid = async (ticketId: string, amount?: number): Promise<IResponse<boolean>> => {
  const response = await httpClient.put<IResponse<boolean>>(
    `tickets/${ticketId}/toggle_paid`,
    amount === undefined ? {} : { amount },
  );

  return response.data;
};

export const getUniqueCount = async (): Promise<IResponse<number>> => {
  const response = await httpClient.get<IResponse<number>>(`tickets/count-unique`);
  return response.data;
};

export const getEvenTicketList = async ([id, page, search]): Promise<IPaginatedResponse<IEventTicketListItem>> => {
  const response = await httpClient.get<IPaginatedResponse<IEventTicketListItem>>(`tickets/${id}`, {
    params: {
      search,
      page,
      limit: 20,
    },
  });
  return response.data;
};
export const getApprovedTicketList = async ([id, page, search]): Promise<IPaginatedResponse<IEventTicketListItem>> => {
  const response = await httpClient.get<IPaginatedResponse<IEventTicketListItem>>(`tickets/${id}/approved`, {
    params: {
      search,
      page,
      limit: 20,
    },
  });
  return response.data;
};

export const getApprovedTicketListForUser = async (
  { id },
  { signal },
): Promise<IResponse<IUserEvenTicketListItem[]>> => {
  const response = await httpClient.get<IResponse<IUserEvenTicketListItem[]>>(`tickets/${id}/history`);
  return response.data;
};

export const exportTicketsToExcel = async (id: string): Promise<void> => {
  try {
    const response = await httpClient.get(`tickets/${id}/export_approved_tickets`, {
      responseType: 'blob',
    });

    // Create a link element
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;

    // Set the file name
    link.setAttribute('download', 'tickets.xlsx');

    // Append to the document and trigger click
    document.body.appendChild(link);
    link.click();

    // Clean up
    document.body.removeChild(link);
  } catch (error) {
    console.error('Error downloading the Excel file:', error);
  }
};

export const getTicketCount = async (
  { id },
  { signal },
): Promise<IResponse<{ approved: number; delivered: number; total: number }>> => {
  const response = await httpClient.get<IResponse<{ approved: number; delivered: number; total: number }>>(
    `tickets/${id}/ticket_counts`,
  );

  return response.data;
};

export const declineTicket = async (eventId: string, ticketId: string): Promise<IResponse<boolean>> => {
  const response = await httpClient.post<IResponse<boolean>>(`tickets/${eventId}/decline`, {
    ticketId,
  });

  return response.data;
};

export const approveTicket = async (
  eventId: string,
  body: { ticketId: string; quantity: number },
): Promise<IResponse<boolean>> => {
  const response = await httpClient.post<IResponse<boolean>>(`tickets/${eventId}/approve`, body);

  return response.data;
};

export const createTicket = async (
  eventId: string,
  body: { userId: string; quantity: number },
): Promise<IResponse<boolean>> => {
  const response = await httpClient.post<IResponse<boolean>>(`tickets/${eventId}/create`, body);

  return response.data;
};
